import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { Client } from '~/api/clients/types';

import { useClients, useDownloadClientsXlsx } from '~/api/clients';
import { AlertDialog, Button, useTable } from '~/components';
import { ColumnIds } from '~/constants/table';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { toast } from '~/utils/toast';

import { useClientsTableColumns } from './ClientsTable.hooks';
import { DeleteClientDialog } from './DeleteClientDialog';

export const ClientsTable = () => {
  const { data: clients } = useClients();
  const downloadMutation = useDownloadClientsXlsx();

  const navigate = useNavigate();
  const { t } = useTranslation(['clients', 'common']);

  const deleteClientHandle = AlertDialog.useHandle<Client>();
  const dialogHandles = useMemo(() => ({ deleteClientHandle }), [deleteClientHandle]);

  const columns = useClientsTableColumns({ dialogHandles });

  const [table, Table] = useTable({
    data: clients,
    columns,
    initialState: {
      sorting: [
        {
          id: ColumnIds.clientsName,
          desc: false,
        },
      ],
    },
    enableSortingRemoval: false,
  });

  return (
    <>
      <Table.Root table={table}>
        <Table.Menu>
          <Table.Search />

          <Table.Filters>
            <Table.Filter columnId={ColumnIds.clientsType} type="select" />
          </Table.Filters>

          <Button
            loading={downloadMutation.isPending}
            onClick={() => downloadMutation.mutate(undefined, { onError: () => toast.danger({ description: t('common:error') }) })}
          >
            {t('common:export')}
          </Button>
        </Table.Menu>

        <Table.Grid>
          <Table.Header />
          <Table.Body>
            {(row) => (
              <Table.Row
                actionLabel={row.original.clientType === 'organisation' ? t('clients:overview.rowActions.view') : t('common:actions.edit')}
                onClick={
                  (row.original.clientType === 'organisation' || row.original.contacts[0]) &&
                  ((client) => {
                    if (client.clientType === 'organisation') {
                      navigate(r(routes.showClient, { clientId: client.id }));
                    } else if (client.contacts[0]) {
                      navigate(r(routes.editContact, { clientId: client.id, contactId: client.contacts[0].id }));
                    }
                  })
                }
                row={row}
              />
            )}
          </Table.Body>
        </Table.Grid>

        <Table.Pagination showPageSizeOptions />
      </Table.Root>

      <AlertDialog.Root handle={deleteClientHandle}>
        {({ payload: client }) => client && <DeleteClientDialog client={client} onClose={() => deleteClientHandle.close()} />}
      </AlertDialog.Root>
    </>
  );
};
